import React, { useState } from 'react';
import PropTypes from 'prop-types';

import ArtistPickerField from './ArtistPickerField';
import { fetchArtistOptions } from './ArtistSelectComponents';

const initials = (name) => {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0] || '')
    .join('')
    .toUpperCase();
};

// Multi-artist companion to ArtistPickerField (O-4907): selected artists render
// as removable chips next to an "+ Add artist" trigger that opens the universal
// picker in controlled mode. One artist is added per picker submission — the
// picker is deliberately single-select, so multi-artist fields compose it
// rather than forking it.
//
// value/onChange speak the same array-of-options contract the isMulti selects
// used ({ value, label, ... }), so consumers keep their existing change
// handlers. Picked artists are appended as
// { value, label, permalink, imageUrl }; already-picked artists are skipped.
const ArtistChipsField = ({
  id, value, onChange, addLabel, description, fetchOptions, allowCreate, teamId,
}) => {
  const [open, setOpen] = useState(false);
  const artists = (value || []).filter(Boolean);

  const handleSelect = ({ artist }) => {
    if (artists.some((a) => a.value === artist.id)) return;
    onChange([...artists, {
      value: artist.id,
      label: artist.name,
      permalink: artist.permalink,
      imageUrl: artist.image_thumb_url,
    }]);
  };

  // Already-picked artists stay visible in the results but render disabled
  // with an "Added" badge (the picker's generic option.disabled/badge support).
  const fetchWithPicked = (query, callback) => {
    (fetchOptions || fetchArtistOptions)(query, (opts) => {
      callback((opts || []).map((o) => (
        artists.some((a) => a.value === o.value) ? { ...o, disabled: true, badge: 'Added' } : o
      )));
    });
  };

  return (
    <div className="artist-chips" data-testid="artist-chips-field">
      {artists.map((artist) => {
        const imageUrl = artist.imageUrl || artist.image_thumb_url;
        return (
          <span key={artist.value} className="artist-chip" data-testid="artist-chip">
            {imageUrl ? (
              <img className="artist-chip__avatar" src={imageUrl} alt="" />
            ) : (
              <span className="artist-chip__avatar">{initials(artist.label)}</span>
            )}
            <span className="artist-chip__name">{artist.label}</span>
            <button
              type="button"
              className="artist-chip__remove"
              aria-label={`Remove ${artist.label}`}
              onClick={() => onChange(artists.filter((a) => a.value !== artist.value))}
            >
              <i className="far fa-times" />
            </button>
          </span>
        );
      })}
      <button
        type="button"
        id={id}
        className="artist-chips__add"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid="artist-chips-add"
      >
        <i className="far fa-plus" aria-hidden="true" /> {addLabel}
      </button>
      <ArtistPickerField
        open={open}
        onOpenChange={setOpen}
        description={description}
        fetchOptions={fetchWithPicked}
        allowCreate={allowCreate}
        teamId={teamId}
        onSelect={handleSelect}
      />
    </div>
  );
};

ArtistChipsField.propTypes = {
  id: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    label: PropTypes.string,
  })),
  onChange: PropTypes.func.isRequired,
  addLabel: PropTypes.string,
  description: PropTypes.string,
  fetchOptions: PropTypes.func,
  allowCreate: PropTypes.bool,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

ArtistChipsField.defaultProps = {
  id: undefined,
  value: [],
  addLabel: 'Add artist',
  description: null,
  fetchOptions: undefined,
  allowCreate: true,
  teamId: null,
};

export default ArtistChipsField;
